import {gql} from '@apollo/client';

import {
  AntibodyVersionQuery,
  AntibodyVersionQueryVariables,
} from 'antibodies/hooks/__generated/AntibodyVersionQuery';
import {assert} from 'Asserts';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

type MaybeAccessibleAntibody = AntibodyVersionQuery['antibody'];
export type Antibody = ExcludeInaccessible<MaybeAccessibleAntibody>;

function assertIsAccessibleAntibody(
  maybeAccessibleAntibody: MaybeAccessibleAntibody
): asserts maybeAccessibleAntibody is Antibody {
  assert(!isInaccessible(maybeAccessibleAntibody), 'Unexpected inaccessible antibody');
}

export const ANTIBODY_VERSION_QUERY = gql`
  query AntibodyVersionQuery($antibodyId: StrictString!, $versionId: StrictString!)
  @skipAudit
  @engComponent(name: ANTIBODIES) {
    antibody(antibodyId: $antibodyId) {
      ... on AntibodyNode {
        id
        format {
          ...AntibodyFormatGraphQL
        }
        currentVersionId
        version(versionId: $versionId) {
          ... on AntibodyVersionNode {
            id
            chains {
              refs
              type
              label
              entity {
                ... on RegistryItemInterface {
                  id
                }
                ...InaccessibleInfoGraphQL
              }
            }
            domains {
              refs
              type
              specificity
              label
              entity {
                ... on RegistryItemInterface {
                  id
                }
                ...InaccessibleInfoGraphQL
              }
            }
            outOfSync
            outOfSyncReason
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('AntibodyFormat').fragment}
`;

type UseAntibodyQueryResult =
  | {status: QueryResultType.SUCCESS; antibody: Antibody}
  | {status: QueryResultType.ERROR | QueryResultType.PENDING | QueryResultType.SKIPPED; antibody: null};

export function useAntibodyVersionQuery(
  antibodyId: string,
  versionId: string | null
): UseAntibodyQueryResult {
  const result = useSkippableBenchlingQuery<AntibodyVersionQuery, AntibodyVersionQueryVariables>(
    ANTIBODY_VERSION_QUERY,
    {
      variables: {antibodyId, versionId: versionId!},
      skip: versionId == null,
    }
  );

  if (result.type !== QueryResultType.SUCCESS) {
    return {status: result.type, antibody: null};
  }

  const antibody = result.data.antibody;
  assertIsAccessibleAntibody(antibody);

  return {status: result.type, antibody};
}
